
































































import { Component, Prop, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
// components
import StageSettings from "@/components/stages/StageSettings.vue";
import StageContent from "@/components/stages/StageContent.vue";
import UnsavedFormModal from "@/components/common/modals/UnsavedFormModal.vue";
// service methods
import { createStage, getStage, updateStage } from "@/services/stages/stageService";
import { getSetting, setSetting } from "@/services/autoSettings/autoSettingsService";
// types
import i18n from "@/i18n";
import { Stage, Content, Settings, ContentType } from "@/services/stages/types";
import { FormComponent, FormComponentModel } from "@/services/autoSettings/types";
import { Guid } from "guid-typescript";
import { bus } from "@/bus";
// validation
import { validateForm } from "@/services/validation/validationService";
import HeaderActionButtons from "@/components/layouts/HeaderActionButtons.vue";

@Component<StageForm>({
    beforeRouteLeave(to, from, next) {
        this.onBeforeRouteLeave(to, next);
    },
    components: {
        StageContent,
        StageSettings,
        HeaderActionButtons,
        UnsavedFormModal
    }
})
export default class StageForm extends Vue {
    @Prop() private stageId!: string;
    // local state
    stage = {
        name: "",
        width: 0,
        height: 0,
        contentTimeout: 15,
        expandedWidth: 0,
        expandedHeight: 0,
        useExpandedDimensionsForTemplate: false,
        stageContent: [] as Content[]
    } as Stage;

    // used for comparing if form have changes
    priorStageState = JSON.parse(JSON.stringify(this.stage)) as Stage;
    formState = false;
    content = [] as Content[];
    componentsData = [] as FormComponentModel[];
    nextRoute = "";
    saveClicked = false;
    continue = false;
    routeParams = {};
    localId = this.stageId || "";

    // lifecycle hooks
    created() {
        this.fetchData();
        this.initComponents();
    }

    // computed
    get settings(): Settings {
        return {
            height: this.stage.height,
            name: this.stage.name,
            width: this.stage.width,
            contentTimeout: this.stage.contentTimeout,
            expandedHeight: this.stage.expandedHeight,
            expandedWidth: this.stage.expandedWidth,
            useExpandedDimensionsForTemplate: this.stage.useExpandedDimensionsForTemplate
        } as Settings;
    }

    get currentContent(): Content[] {
        if (!this.stage.stageContent) return [] as Content[];

        const contentArray = [] as Content[];
        this.stage.stageContent.forEach(x => {
            const content = {} as Content;

            content.contentId = (x.channelGroupId || x.channelId || x.templateId) as string;
            content.id = x.id ? x.id : Guid.create().toString();
            content.name = x.name;
            content.active = x.active;
            content.interactionBehavior = x.interactionBehavior;
            content.returnToFirstPostTime = x.returnToFirstPostTime;
            content.showFirstPost = x.showFirstPost;
            content.menuStyleId = x.menuStyleId;
            content.isDefault = x.isDefault;
            content.contentType = x.contentType;
            content.eventName = x.eventName;

            contentArray.push(content);
        });
        return contentArray;
    }

    get components(): FormComponentModel[] {
        return this.componentsData.sort((x, y) => x.order - y.order);
    }

    get componentsAutoSettingKey(): string {
        let settingsKey = "stageFormComponents";
        settingsKey += this.localId ? "-edit" : "-create";
        return settingsKey;
    }

    // methods
    async initComponents() {
        let components = getSetting(this.componentsAutoSettingKey);
        if (!components) {
            await setSetting(this.componentsAutoSettingKey,
                [
                    {
                        componentName: "StageSettings",
                        sectionName: i18n.t("common.settings"),
                        order: 0,
                        collapseState: true
                    },
                    {
                        componentName: "StageContent",
                        sectionName: i18n.t("common.content"),
                        order: 2,
                        collapseState: true
                    }]);
            components = getSetting(this.componentsAutoSettingKey);
        }

        this.componentsData = components.map((x: FormComponent) => ({
            componentName: x.componentName,
            order: x.order,
            active: false,
            activeDropTarget: false,
            collapseState: x.collapseState,
            sectionName: x.sectionName
        }));
    }

    onBeforeRouteLeave(to: Route, next: any) {
        if (this.saveClicked || this.continue) {
            next();
        } else {
            this.nextRoute = to.name ? to.name : "";
            this.routeParams = to.params;
            this.retrieveData();
            this.setStageDto();

            // we remap and sort the arrays because the order is messed up during the reassignment of the data
            const stageCopy = JSON.parse(JSON.stringify(this.stage)) as Stage;
            stageCopy.stageContent.sort((a: Content, b: Content) => { return (a.id < b.id) ? -1 : (a.id > b.id) ? 1 : 0; });
            const priorStageStateCopy = JSON.parse(JSON.stringify(this.priorStageState)) as Stage;
            priorStageStateCopy.stageContent.sort((a: Content, b: Content) => { return (a.id < b.id) ? -1 : (a.id > b.id) ? 1 : 0; });
            const noChanges = this.lodash.isEqual(priorStageStateCopy, stageCopy);
            if (noChanges) {
                next();
            } else {
                next(false);
                (this.$refs.UnsavedFormModal as any).open();
            }
        }
    }

    onContinue(data: boolean) {
        this.continue = data;
        if (data) this.$router.push({ name: this.nextRoute, params: this.routeParams });
        this.nextRoute = "";
    }

    async save(back: boolean) {
        this.saveClicked = true;
        await this.childComponentsHandler();
        if (!this.formState) {
            this.saveClicked = false;
            return;
        }

        this.setStageDto();
        if (!this.localId) {
            createStage(this.stage).then(id => {
                if (back) this.$router.push("/stages");
                else {
                    this.localId = id;
                    this.stage.id = id;
                    this.saveClicked = false;
                    this.priorStageState = JSON.parse(JSON.stringify(this.stage)) as Stage;
                }
            });
            return;
        }

        updateStage(this.stage).then(response => {
            if (back) this.$router.push("/stages");
            this.saveClicked = false;
            this.priorStageState = JSON.parse(JSON.stringify(this.stage)) as Stage;
        });
    }

    setStageDto() {
        this.stage.stageContent = this.content && this.content.map(x => ({
            id: x.id,
            name: x.name,
            channelId: x.contentType === ContentType.Channel ? x.contentId : null,
            channelGroupId: x.contentType === ContentType.ChannelGroup ? x.contentId : null,
            templateId: x.contentType === ContentType.Design ? x.contentId : null,
            isDefault: x.isDefault,
            eventName: x.eventName,
            active: x.active,
            interactionBehavior: x.interactionBehavior,
            returnToFirstPostTime: x.returnToFirstPostTime,
            showFirstPost: x.showFirstPost,
            menuStyleId: x.menuStyleId,
            contentType: x.contentType
        })) as Content[];
    }

    async childComponentsHandler() {
        const validators = [] as any[];
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            validators.push(component.validators);
            component.emitData();
        });

        this.formState = await validateForm(validators);
    }

    retrieveData() {
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            component.emitData();
        });
    }

    fetchData() {
        if (this.localId) {
            getStage(this.localId)
                .then(response => {
                    this.stage = response;
                    this.priorStageState = JSON.parse(JSON.stringify(this.stage));
                    bus.$emit("documentTitle", this.stage.name);
                });
        }
    }

    // events && callbacks
    onSettingsUpdated(settings: Settings) {
        this.stage.name = settings.name || "";
        this.stage.width = settings.width || 0;
        this.stage.height = settings.height || 0;
        this.stage.expandedWidth = settings.expandedWidth || 0;
        this.stage.expandedHeight = settings.expandedHeight || 0;
        this.stage.useExpandedDimensionsForTemplate = settings.useExpandedDimensionsForTemplate || false;
        this.stage.contentTimeout = settings.contentTimeout || 15;
    }

    onContentUpdated(content: Content[]) {
        this.content = content;
    }

    onComponentStateChanged(componentState: any) {
        const component = this.componentsData.find(x => x.componentName === componentState.name);
        if (!component) return;

        component.collapseState = componentState.state;
        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => (
                {
                    componentName: x.componentName,
                    order: x.order,
                    collapseState: x.collapseState,
                    sectionName: x.sectionName
                } as FormComponent)));
    }

    // drag & drop event handlers
    drag(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.target.id);
        if (!component) { return; }

        event.dataTransfer.setDragImage((this.$refs[component.componentName] as any)[0], 1000, 0);
        event.dataTransfer.setData(
            "id",
            event.target.id
        );

        component.active = true;
    }

    dragLeave(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        component.activeDropTarget = false;
    }

    dragOver(event: any) {
        event.preventDefault();
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        if (component.active) return;
        component.activeDropTarget = true;
    }

    dragEnd() {
        this.componentsData.forEach(x => {
            x.activeDropTarget = false;
            x.active = false;
        });
    }

    swapComponents(event: any) {
        event.preventDefault();
        if (!event.currentTarget) {
            return;
        }

        const target = this
            .componentsData
            .find(x => event.currentTarget && x.componentName === event.currentTarget.id);
        const source = this
            .componentsData
            .find(x => event.dataTransfer && x.componentName === event.dataTransfer.getData("id"));

        if (!(source && target)) { return; }

        const sourceOrder = source.order;
        source.order = target.order;
        target.order = sourceOrder;

        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({
                componentName: x.componentName,
                order: x.order,
                collapseState: x.collapseState,
                sectionName: x.sectionName
            } as FormComponent)));
    }

    back() {
        window.history.go(-1);
    }
}
